import { memo, useContext, useMemo, type ReactNode } from "react";
import { MessageImagesContext, useAgentImageViewer } from "./agentImages";
import { parseFileLink } from "./fileLinks";
import { useFileOpener } from "./fileOpener";
import { messageAttachmentTarget } from "./messageAttachments";
import { parseMarkdown, splitStreamingMarkdown, type BlockNode, type InlineNode } from "./markdown";

/**
 * Renders agent chat text as Markdown. Only the node types our parser produces
 * are ever emitted — no raw HTML reaches the DOM.
 *
 * `streaming` says the text is still arriving, which means it is a different
 * string on every animation frame and memoizing the whole parse buys nothing.
 * Only the last block of a growing message can still change, so the settled head
 * is parsed once and only the tail is paid for per frame.
 */
export const Markdown = memo(function Markdown({
  text,
  streaming = false,
  className,
}: {
  text: string;
  streaming?: boolean;
  className?: string;
}) {
  const split = streaming ? splitStreamingMarkdown(text) : null;
  const headBlocks = useMemo(() => (split ? parseMarkdown(split.head) : []), [split?.head]);
  const tailBlocks = useMemo(
    () => parseMarkdown(split ? split.tail : text),
    [split ? split.tail : text],
  );
  const blocks = split ? [...headBlocks, ...tailBlocks] : tailBlocks;
  return <div className={className ? `md ${className}` : "md"}>{renderBlocks(blocks)}</div>;
});

function renderBlocks(blocks: BlockNode[]): ReactNode {
  return blocks.map((block, index) => <Block key={index} block={block} />);
}

function Block({ block }: { block: BlockNode }): ReactNode {
  switch (block.type) {
    case "paragraph":
      return <p className="md-p">{renderInline(block.children)}</p>;
    case "heading": {
      const Tag = `h${Math.min(block.level + 1, 6)}` as "h2";
      return <Tag className={`md-h md-h${block.level}`}>{renderInline(block.children)}</Tag>;
    }
    case "code":
      return (
        <pre className="md-pre" data-language={block.language ?? undefined}>
          <code>{block.value}</code>
        </pre>
      );
    case "quote":
      return <blockquote className="md-quote">{renderBlocks(block.children)}</blockquote>;
    case "list":
      return block.ordered ? (
        <ol className="md-list" start={block.start}>
          {block.items.map((item, index) => (
            <li key={index}>{renderBlocks(item)}</li>
          ))}
        </ol>
      ) : (
        <ul className="md-list">
          {block.items.map((item, index) => (
            <li key={index}>{renderBlocks(item)}</li>
          ))}
        </ul>
      );
    case "table":
      // The wrapper scrolls sideways so a wide table never widens the transcript.
      return (
        <div className="md-table-wrap">
          <table className="md-table">
            <thead>
              <tr>
                {block.header.map((cell, index) => (
                  <th key={index} style={{ textAlign: block.align[index] }}>
                    {renderInline(cell)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.rows.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  {row.map((cell, index) => (
                    <td key={index} style={{ textAlign: block.align[index] }}>
                      {renderInline(cell)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    case "rule":
      return <hr className="md-rule" />;
  }
}

function renderInline(nodes: InlineNode[]): ReactNode {
  return nodes.map((node, index) => <Inline key={index} node={node} />);
}

function Inline({ node }: { node: InlineNode }): ReactNode {
  switch (node.type) {
    case "text":
      return node.value;
    case "break":
      return <br />;
    case "code":
      // A screenshot the agent saved and named opens like a cited file.
      return messageAttachmentTarget(node.value) ? (
        <FileLink href={node.value.trim()}><code className="md-code">{node.value}</code></FileLink>
      ) : (
        <code className="md-code">{node.value}</code>
      );
    case "strong":
      return <strong>{renderInline(node.children)}</strong>;
    case "em":
      return <em>{renderInline(node.children)}</em>;
    case "strike":
      return <s>{renderInline(node.children)}</s>;
    case "link":
      return parseFileLink(node.href) ? (
        <FileLink href={node.href}>{renderInline(node.children)}</FileLink>
      ) : (
        <a className="md-link" href={node.href} target="_blank" rel="noreferrer noopener">
          {renderInline(node.children)}
        </a>
      );
  }
}

/** A path is not a URL: handed to the browser it opens a dead tab. In a workspace
 * it opens the file there instead, and anywhere else it is only a label. */
function FileLink({ href, children }: { href: string; children: ReactNode }) {
  const openFile = useFileOpener();
  const viewer = useAgentImageViewer();
  const images = useContext(MessageImagesContext);
  const target = parseFileLink(href)!;
  if (!openFile) return <span>{children}</span>;
  // One of the answer's own images opens in the lightbox, like its preview.
  const imageIndex = viewer ? images.findIndex((image) => image.path === target.path) : -1;
  return (
    <a
      className="md-link"
      href={href}
      title={href}
      onClick={(event) => {
        event.preventDefault();
        const inNewTab = event.metaKey || event.ctrlKey;
        if (viewer && imageIndex >= 0 && !inNewTab) viewer.open(images, imageIndex);
        else openFile(target, inNewTab);
      }}
    >
      {children}
    </a>
  );
}
